// frontEnd/src/Component/SpendingCharts/SpendingCharts.js
import React, { useEffect, useMemo, useState } from "react";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from "recharts";
import "./SpendingCharts.css";
const REACT_APP_EXPRESS_API = process.env.REACT_APP_EXPRESS_API;

/**
 * Pakai ENV seperti komponen lain:
 * - REACT_APP_API_URL bisa diisi "http://localhost:5001" (tanpa /api/transactions)
 * - fallback ke localhost bila ENV belum diset
 */
const BASE_URL =
  (typeof process !== "undefined" &&
    process.env &&
    process.env.REACT_APP_API_URL) ||
  `${REACT_APP_EXPRESS_API}`;
const API_URL = `${String(BASE_URL).replace(/\/$/, "")}/api/transactions`;

// ===== util normalize =====
const pick = (obj, keys) => keys.find((k) => obj?.[k] !== undefined);

const normalizeItem = (t) => {
  if (!t || typeof t !== "object") return null;
  const nominalKey = pick(t, ["nominal", "amount", "total"]);
  const jenisKey = pick(t, ["jenis_kebutuhan", "jenis", "type", "category_2"]);
  const katKey = pick(t, ["kategori_pengeluaran", "kategori", "category", "category_1"]);
  const dateKey = pick(t, ["tanggal", "date", "createdAt", "updatedAt"]);
  return {
    nominal: Number(t[nominalKey] ?? 0),
    jenis_kebutuhan: String(t[jenisKey] ?? "").trim(),
    kategori_pengeluaran: String(t[katKey] ?? "").trim(),
    tanggal: t[dateKey] ? new Date(t[dateKey]) : null,
  };
};

const normalizeList = (raw) => {
  const list = Array.isArray(raw)
    ? raw
    : Array.isArray(raw?.transactions)
      ? raw.transactions
      : Array.isArray(raw?.data)
        ? raw.data
        : [];
  return list.map(normalizeItem).filter(Boolean);
};

const groupSum = (rows, key) => {
  const m = new Map();
  rows.forEach((r) => {
    const k = (r[key] || "Lainnya").trim() || "Lainnya";
    m.set(k, (m.get(k) || 0) + (Number(r.nominal) || 0));
  });
  return Array.from(m.entries())
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);
};

// ===== color maps (sesuai mockup) =====
const NEEDS_COLOR_MAP = {
  "Makanan & Kebutuhan Rumah Tangga": "#38BDF8",   // cyan
  "Transportasi": "#FB923C",                        // orange
  "Konektivitas": "#F59E0B",                        // amber
  "Tagihan & Lainnya": "#3B82F6",                   // blue
  "Hiburan": "#1E3A8A",                             // navy
  "Internet": "#9CA3AF",                            // gray
};

// urutan kategori kebutuhan sesuai mockup
const ORDER_KATEGORI = [
  "Makanan & Kebutuhan Rumah Tangga",
  "Transportasi",
  "Konektivitas",
  "Tagihan & Lainnya",
  "Hiburan",
];

const EXPENSE_COLOR_MAP = {
  Primer: "#10B981",     // green (lebih terang)
  Sekunder: "#FFC107",   // yellow/gold
  Tersier: "#EF4444",    // red
};

const ORDER_JENIS = ["Primer", "Sekunder", "Tersier"];

const FALLBACK = ["#38BDF8", "#FB923C", "#F59E0B", "#3B82F6", "#1E3A8A", "#9CA3AF"];

const currency = (n) =>
  `IDR ${Number(n || 0).toLocaleString("id-ID", { maximumFractionDigits: 0 })}`;

const TooltipBox = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const p = payload[0];
    return (
      <div className="sc-tooltip">
        <div className="sc-tooltip-title">{p.name}</div>
        <div className="sc-tooltip-value">{currency(p.value)}</div>
      </div>
    );
  }
  return null;
};

// ===== helper bulan seperti di BudgetCards =====
const monthKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
const monthLabel = (key) => {
  const [y, m] = key.split("-");
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleDateString("id-ID", { month: "long", year: "numeric" });
};
const compareMonthKey = (a, b) => (a < b ? -1 : a > b ? 1 : 0);

export default function SpendingCharts({ transactions, data, layout = "default" }) {
  const [fetched, setFetched] = useState([]);
  const [error, setError] = useState("");

  // dropdown bulan
  const [monthOptions, setMonthOptions] = useState([]); // ["2025-01", ...]
  const [startMonth, setStartMonth] = useState("");
  const [endMonth, setEndMonth] = useState("");

  // fetch jika parent tidak kirim data
  useEffect(() => {
    if (Array.isArray(transactions) || Array.isArray(data)) return;

    (async () => {
      try {
        setError(""); // reset error state
        const res = await fetch(API_URL, {
          method: "GET",
          credentials: "include", // ✅ kirim cookie ke backend
          headers: { Accept: "application/json" },
        });

        const raw = await res.json();
        if (!res.ok) throw new Error(raw?.message || "Gagal memuat transaksi");

        setFetched(normalizeList(raw));
      } catch (e) {
        setError(e?.message || "Terjadi kesalahan saat memuat data");
      }
    })();
  }, [transactions, data]);

  const base = useMemo(() => {
    const fromProps = normalizeList(transactions ?? data ?? []);
    return fromProps.length ? fromProps : fetched;
  }, [transactions, data, fetched]);

  // bangun opsi bulan dari data
  useEffect(() => {
    const months = new Set();
    base.forEach((x) => {
      if (x.tanggal instanceof Date) months.add(monthKey(x.tanggal));
    });
    const arr = Array.from(months).sort(compareMonthKey);
    setMonthOptions(arr);
    if (arr.length) {
      setStartMonth(arr[0]);
      setEndMonth(arr[arr.length - 1]);
    } else {
      setStartMonth("");
      setEndMonth("");
    }
  }, [base]);

  // filter data sesuai rentang bulan (inklusif)
  const scoped = useMemo(() => {
    if (!startMonth || !endMonth) return base.filter((x) => x.tanggal instanceof Date);
    const [sY, sM] = startMonth.split("-").map(Number);
    const [eY, eM] = endMonth.split("-").map(Number);
    const start = new Date(sY, sM - 1, 1, 0, 0, 0, 0);
    const end = new Date(eY, eM, 0, 23, 59, 59, 999);
    return base.filter(
      (x) => x.tanggal instanceof Date && x.tanggal >= start && x.tanggal <= end
    );
  }, [base, startMonth, endMonth]);

  // kebutuhan (kategori_pengeluaran) - dengan urutan tetap
  const byKategori = useMemo(() => {
    const grouped = groupSum(scoped, "kategori_pengeluaran");
    const map = Object.fromEntries(grouped.map((g) => [g.name, g.value]));
    return ORDER_KATEGORI
      .map((name) => ({ name, value: map[name] || 0 }))
      .filter((x) => x.value > 0);
  }, [scoped]);

  // pengeluaran (jenis_kebutuhan Primer/Sekunder/Tersier)
  const byJenis = useMemo(() => {
    const allowed = new Set(ORDER_JENIS);
    const raw = scoped
      .map((x) => ({ ...x, jenis_kebutuhan: cap(x.jenis_kebutuhan) }))
      .filter((x) => allowed.has(x.jenis_kebutuhan));
    const grouped = groupSum(raw, "jenis_kebutuhan");
    const map = Object.fromEntries(grouped.map((g) => [g.name, g.value]));
    return ORDER_JENIS.map((name) => ({ name, value: map[name] || 0 })).filter((x) => x.value > 0);
  }, [scoped]);

  const totalKategori = byKategori.reduce((s, r) => s + r.value, 0) || 1;
  const totalJenis = byJenis.reduce((s, r) => s + r.value, 0) || 1;

  if (error) return <div className="sc-grid"><div className="sc-card">{error}</div></div>;
  if (!base.length) return <div className="sc-grid"><div className="sc-card">Tidak ada data untuk grafik.</div></div>;

  // Dropdown range (gantikan chips)
  const RangeSelectors = (
    <div className="sc-range" style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
      <div className="sc-range-field" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <label className="sc-range-label" style={{ fontSize: 12, color: "#374151" }}>
          Start Date
        </label>
        <select
          className="sc-range-select"
          style={{
            height: 32,
            padding: "0 10px",
            border: "1px solid #e5e7eb",
            borderRadius: 8,
            background: "#fff",
            fontSize: 13,
            color: "#111827",
            outline: "none",
          }}
          value={startMonth}
          onChange={(e) => setStartMonth(e.target.value)}
        >
          {monthOptions.map((m) => (
            <option key={m} value={m}>
              {monthLabel(m)}
            </option>
          ))}
        </select>
      </div>
      <div className="sc-range-field" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <label className="sc-range-label" style={{ fontSize: 12, color: "#374151" }}>
          End Date
        </label>
        <select
          className="sc-range-select"
          style={{
            height: 32,
            padding: "0 10px",
            border: "1px solid #e5e7eb",
            borderRadius: 8,
            background: "#fff",
            fontSize: 13,
            color: "#111827",
            outline: "none",
          }}
          value={endMonth}
          onChange={(e) => setEndMonth(e.target.value)}
        >
          {monthOptions.map((m) => (
            <option key={m} value={m}>
              {monthLabel(m)}
            </option>
          ))}
        </select>
      </div>
    </div>
  );

  const KebutuhanCard = (
    <div className="sc-card">
      <div className="sc-title">Kategori Pengeluaran Berdasarkan Kategori</div>
      <div className="sc-filter-row">{RangeSelectors}</div>

      <div className="sc-chart-wrap">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={byKategori}
              dataKey="value"
              nameKey="name"
              innerRadius="55%"
              outerRadius="85%"
              stroke="#fff"
              strokeWidth={2}
              paddingAngle={1}
            >
              {byKategori.map((it, i) => (
                <Cell
                  key={it.name || i}
                  fill={NEEDS_COLOR_MAP[it.name] || FALLBACK[i % FALLBACK.length]}
                />
              ))}
            </Pie>
            <Tooltip content={<TooltipBox />} />
          </PieChart>
        </ResponsiveContainer>
      </div>

      <ul className="sc-legend">
        {byKategori.map((it, i) => (
          <li key={it.name || i}>
            <span
              className="sc-dot"
              style={{ backgroundColor: NEEDS_COLOR_MAP[it.name] || FALLBACK[i % FALLBACK.length] }}
            />
            <span className="sc-legend-label">{it.name || "Lainnya"}</span>
            <span className="sc-legend-value">
              {Math.round((it.value / totalKategori) * 100)}%
            </span>
          </li>
        ))}
      </ul>
    </div>
  );

  const PengeluaranCard = (
    <div className="sc-card">
      <div className="sc-title">Kategori Pengeluaran Berdasarkan Kebutuhan</div>
      <div className="sc-filter-row">{RangeSelectors}</div>

      <div className="sc-chart-wrap">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={byJenis}
              dataKey="value"
              nameKey="name"
              outerRadius="85%"
              stroke="#fff"
              strokeWidth={2}
              paddingAngle={1}
            >
              {byJenis.map((d, i) => (
                <Cell key={d.name} fill={EXPENSE_COLOR_MAP[d.name] || FALLBACK[i % FALLBACK.length]} />
              ))}
            </Pie>
            <Tooltip content={<TooltipBox />} />
          </PieChart>
        </ResponsiveContainer>
      </div>

      <ul className="sc-legend">
        {ORDER_JENIS.map((name, i) => {
          const row = byJenis.find((x) => x.name === name);
          if (!row) return null;
          return (
            <li key={name}>
              <span className="sc-dot" style={{ backgroundColor: EXPENSE_COLOR_MAP[name] || FALLBACK[i % FALLBACK.length] }} />
              <span className="sc-legend-label">{name}</span>
              <span className="sc-legend-value">
                {Math.round((row.value / totalJenis) * 100)}%
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );

  return (
    <div className="sc-grid">
      {layout === "pengeluaran-left" ? (
        <>
          {PengeluaranCard}
          {KebutuhanCard}
        </>
      ) : (
        <>
          {KebutuhanCard}
          {PengeluaranCard}
        </>
      )}
    </div>
  );
}

function cap(s = "") {
  const t = s.toLowerCase();
  if (t === "primer") return "Primer";
  if (t === "sekunder") return "Sekunder";
  if (t === "tersier") return "Tersier";
  return s;
}
